// SEM-5308 / C6 - tenant audit-log adapter. Backend contract:
// services/platform-graph/src/routes/audit-events.ts.
//
// The generated route contract already includes its filters and response, so
// this adapter stays fully typed. The wire cursor is snake_case because the
// handler returns next_cursor; map it once to the canonical KeysetPage shape
// so the shared useKeysetQuery hook owns all pagination behavior. Only field
// names cross this boundary through changed_fields; no before/after values are
// projected or reconstructed in the customer console.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope } from "@/api/errors";
import type { KeysetPage } from "@/api/keyset";

export interface AuditEventRow {
  readonly audit_event_id: string;
  readonly ts: string;
  readonly source_spec: string;
  readonly actor_kind: string;
  readonly actor_id: string | null;
  readonly actor_display_name: string | null;
  readonly actor_id_short: string | null;
  /** The employee behind the actor identity, when one resolved — the link target. */
  readonly actor_employee_id: string | null;
  readonly actor_resolution: "resolved" | "unresolved" | "redacted" | "not_applicable";
  readonly action: string;
  readonly entity_kind: string;
  readonly entity_id: string;
  readonly entity_display_name: string | null;
  readonly entity_tombstoned: boolean;
  readonly changed_fields: readonly string[];
  readonly ip: string | null;
  readonly user_agent: string | null;
  readonly trace_id: string | null;
}

/**
 * The filter set the SERVER accepts, named once. Every one of these was
 * already implemented in routes/audit-events.ts and unreachable from the
 * screen, which passed `action` alone (SEM-6253).
 *
 * `from`/`to` are ISO instants, not date-only: the server coerces to a Date
 * and compares against `ts`, so a bare "2026-08-06" would silently mean
 * midnight UTC and drop the whole day the user meant to include.
 */
export interface AuditEventFilters {
  readonly action?: string;
  readonly entityKind?: string;
  readonly actorId?: string;
  readonly sourceSpec?: string;
  readonly from?: string;
  readonly to?: string;
}

function toQuery(filters: AuditEventFilters): Record<string, string> {
  return {
    ...(filters.action !== undefined && { action: filters.action }),
    ...(filters.entityKind !== undefined && { entity_kind: filters.entityKind }),
    ...(filters.actorId !== undefined && { actor_id: filters.actorId }),
    ...(filters.sourceSpec !== undefined && { source_spec: filters.sourceSpec }),
    ...(filters.from !== undefined && { from: filters.from }),
    ...(filters.to !== undefined && { to: filters.to }),
  };
}

export async function fetchAuditEventsPage(
  filters: AuditEventFilters,
  cursor: string | null,
  limit = 50,
): Promise<KeysetPage<AuditEventRow>> {
  const page = await unwrapEnvelope(
    platformGraph.GET("/v1/audit-events", {
      params: {
        query: {
          limit,
          ...toQuery(filters),
          ...(cursor !== null && { cursor }),
        },
      },
    }),
  );
  return { items: page.events, nextCursor: page.next_cursor };
}

/** The server's own per-request ceiling (routes/audit-events.ts query schema). */
export const AUDIT_PAGE_LIMIT_MAX = 200;

/**
 * How many rows one export may pull. Ten full server pages.
 *
 * A cap has to exist — the audit ledger is a ten-year append-only table and
 * "export everything" is an unbounded request against it. The number matters
 * less than the two rules around it: the caller is TOLD when the cap bit
 * (`truncated`), and the screen shows it. A CSV that quietly stops at row
 * 2000 reads to an auditor as "these are all the events", which is the one
 * lie an audit export must never tell.
 */
export const AUDIT_EXPORT_ROW_CAP = 2_000;

export interface AuditExportResult {
  readonly rows: readonly AuditEventRow[];
  /** True when the cap stopped the fetch while the server still had pages. */
  readonly truncated: boolean;
}

/**
 * Pulls the current filter's result set for export, through the SAME endpoint
 * the table reads.
 *
 * Reusing the read path is the point, not a convenience: tombstone redaction
 * is applied at read time by the route (`createTombstoneRedactor`), so a
 * second, "more direct" export query is exactly how an erased employee's data
 * would reach a CSV. There is no export-specific query, and there should
 * never be one.
 */
export async function fetchAuditEventsForExport(
  filters: AuditEventFilters,
  // Injected rather than closed over so the cap/truncation rules below can be
  // asserted directly. They are the safety property of this function, and a
  // screen test that mocks this whole function cannot see them at all.
  options: {
    readonly cap?: number;
    readonly fetchPage?: typeof fetchAuditEventsPage;
  } = {},
): Promise<AuditExportResult> {
  const cap = options.cap ?? AUDIT_EXPORT_ROW_CAP;
  const fetchPage = options.fetchPage ?? fetchAuditEventsPage;
  const rows: AuditEventRow[] = [];
  let cursor: string | null = null;

  do {
    const page: KeysetPage<AuditEventRow> = await fetchPage(
      filters,
      cursor,
      AUDIT_PAGE_LIMIT_MAX,
    );
    rows.push(...page.items);
    cursor = page.nextCursor;
    if (rows.length >= cap) {
      // Report truncation only when the server actually had more to give.
      // Landing exactly on the cap with no next cursor is a COMPLETE export,
      // and warning about it would train the reader to ignore the warning.
      return { rows: rows.slice(0, cap), truncated: cursor !== null };
    }
  } while (cursor !== null);

  return { rows, truncated: false };
}
